import { getCookie, setCookie } from './cookies';
import { getURLParam } from './url';

/**
 * Super Cookie v1 Schema
 * Consolidates multiple attribution/UTM cookies into a single structured cookie.
 * See: https://stashinvest.atlassian.net/wiki/spaces/PRODUCTDELIVERY/pages/5416976438/What+is+a+Super+Cookie
 */
export interface SuperCookieV1 {
	v: 1;
	promoCode?: string;
	legacyPromoCode?: string;
	theme?: string;
	channel?: string;
	mixName?: string;
	prompt?: string;
	frontend_platform: 'web';
	fbclid?: string;
	_fbp?: string;
	_fbc?: string;
	gclid?: string;
	irclickid?: string;
	ttclid?: string;
	rdt_cid?: string;
	utm_campaign?: string;
	utm_source?: string;
	utm_medium?: string;
	utm_content?: string;
	utm_term?: string;
	/** UTC ISO-8601; set when campaign identity or a paid click ID changes. */
	utm_timestamp?: string;
	/** `${utm_source}|${utm_campaign}` at the time utm_timestamp was written. */
	utm_timestamp_src?: string;
	/** Client-only (redirect / Paul Revere). Not sent on entry signals (STASH2026-4905). */
	utm_provider?: string;
}

export const SUPER_COOKIE_NAME = 'stashCookie';
export const SESSION_STARTED_EVENT = 'Session_Started';
export const SESSION_ATTRIBUTION_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const TIMESTAMP_TRIGGER_UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign'] as const;

const SESSION_TRIGGER_CLICK_ID_KEYS = [
	'gclid',
	'fbclid',
	'irclickid',
	'ttclid',
	'rdt_cid',
] as const;

type SessionTriggerSnapshot = Partial<
	Pick<SuperCookieV1, (typeof TIMESTAMP_TRIGGER_UTM_KEYS)[number]> &
		Pick<SuperCookieV1, (typeof SESSION_TRIGGER_CLICK_ID_KEYS)[number]> &
		Pick<SuperCookieV1, 'promoCode' | 'legacyPromoCode'>
>;

export type CreateSuperCookieOptions = {
	now?: Date;
};

export type CreateSuperCookieResult = {
	cookie: SuperCookieV1 | null;
	shouldEmitSessionStarted: boolean;
};

/**
 * Gets and parses the super cookie from the browser.
 * @returns Parsed super cookie object or null if not found/invalid
 */
export function getSuperCookie(): SuperCookieV1 | null {
	const raw = getCookie(SUPER_COOKIE_NAME);
	if (!raw) {
		return null;
	}

	try {
		// Decode the cookie value (some repos encode JSON strings for RFC 6265 compliance)
		// Try decoding first, fall back to raw if it's not encoded
		let decoded: string;
		try {
			decoded = decodeURIComponent(raw);
		} catch {
			// If decodeURIComponent fails, the value wasn't encoded, use raw
			decoded = raw;
		}
		const parsed = JSON.parse(decoded) as SuperCookieV1;
		if (parsed.v !== 1) {
			console.warn('Unknown super cookie version:', parsed.v);
			return null;
		}
		return parsed;
	} catch (e) {
		console.warn('Failed to parse super cookie:', e);
		return null;
	}
}

// `getSuperCookie()` casts parsed JSON without validating field types, so a
// malformed cookie could carry a non-string promo value. Guard against it so a
// bad payload does not throw and block the redirect / individual-cookie fallback.
function normalizePromo(value: unknown): string | undefined {
	const trimmed = typeof value === 'string' ? value.trim() : undefined;
	return trimmed ? trimmed : undefined;
}

// `getURLParam()` decodes via `decodeURIComponent`, which throws on malformed
// percent-encoding (e.g. `?stash_code_v2=%E0%A4%A`). Swallow it so a bad URL
// falls through to the super-cookie / individual-cookie sources instead of
// throwing and blocking the redirect.
function getPromoParam(name: string): string | undefined {
	try {
		return normalizePromo(getURLParam(name));
	} catch {
		return undefined;
	}
}

/**
 * Resolves promo codes for outbound CTA / deep-link URLs, in priority order:
 * URL query param (freshest, last-touch), then the super cookie, then the
 * individual `stashPromoCodeV2` / `stashPromoCode` cookies that upstream
 * landing pages are retiring during cookie consolidation (Attribution Audit,
 * Edge Case F).
 *
 * Reading the URL param directly keeps the promo on the deep link even before
 * STASH_INIT has folded it into the super cookie, and in in-app webviews where
 * a cross-site cookie write to `.stash.com` may have been blocked. Blank or
 * non-string values are treated as absent so a lower-priority source applies.
 */
export function getPromoCodes(): {
	promoCode: string | undefined;
	legacyPromoCode: string | undefined;
} {
	const superCookie = getSuperCookie();
	return {
		promoCode:
			getPromoParam('stash_code_v2') ??
			normalizePromo(superCookie?.promoCode) ??
			normalizePromo(getCookie('stashPromoCodeV2')),
		legacyPromoCode:
			getPromoParam('stash_code') ??
			normalizePromo(superCookie?.legacyPromoCode) ??
			normalizePromo(getCookie('stashPromoCode')),
	};
}

export const SUPER_COOKIE_MAX_AGE_MS = 1000 * 60 * 60 * 24 * 60; // 60 days
const MAX_COOKIE_SIZE_BYTES = 3500;

function utmTimestampSrcForCookie(cookie: SuperCookieV1): string {
	return `${cookie.utm_source ?? ''}|${cookie.utm_campaign ?? ''}`;
}

const ISO_UTC_TIMESTAMP = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/;

export function isTrustedUtmTimestamp(cookie: SuperCookieV1): boolean {
	const ts = cookie.utm_timestamp;
	if (typeof ts !== 'string' || !ISO_UTC_TIMESTAMP.test(ts) || !cookie.utm_timestamp_src) {
		return false;
	}
	const parsed = Date.parse(ts);
	if (Number.isNaN(parsed) || new Date(parsed).toISOString().slice(0, 19) !== ts.slice(0, 19)) {
		return false;
	}
	return cookie.utm_timestamp_src === utmTimestampSrcForCookie(cookie);
}

export function canEmitSessionStartedAfterChange(cookie: SuperCookieV1, now: Date): boolean {
	if (!isTrustedUtmTimestamp(cookie)) {
		return true;
	}
	const ts = Date.parse(cookie.utm_timestamp ?? '');
	if (Number.isNaN(ts)) {
		return true;
	}
	return now.getTime() - ts <= SESSION_ATTRIBUTION_MAX_AGE_MS;
}

function snapshotSessionTriggers(cookie: SuperCookieV1 | null): SessionTriggerSnapshot {
	if (!cookie) {
		return {};
	}
	return {
		utm_source: cookie.utm_source,
		utm_medium: cookie.utm_medium,
		utm_campaign: cookie.utm_campaign,
		gclid: cookie.gclid,
		fbclid: cookie.fbclid,
		irclickid: cookie.irclickid,
		ttclid: cookie.ttclid,
		rdt_cid: cookie.rdt_cid,
		promoCode: cookie.promoCode,
		legacyPromoCode: cookie.legacyPromoCode,
	};
}

function fieldValueChanged(before: string | undefined, after: string | undefined): boolean {
	return (before ?? '') !== (after ?? '');
}

function effectiveShareCode(
	cookie: Pick<SuperCookieV1, 'promoCode' | 'legacyPromoCode'> | null | undefined
): string | undefined {
	return normalizePromo(cookie?.promoCode) ?? normalizePromo(cookie?.legacyPromoCode);
}

function timestampTriggerChanged(
	before: SessionTriggerSnapshot,
	after: SuperCookieV1
): boolean {
	for (const key of TIMESTAMP_TRIGGER_UTM_KEYS) {
		if (fieldValueChanged(before[key], after[key])) {
			return true;
		}
	}
	for (const key of SESSION_TRIGGER_CLICK_ID_KEYS) {
		if (fieldValueChanged(before[key], after[key])) {
			return true;
		}
	}
	return false;
}

export function buildSessionStartedProperties(
	cookie: SuperCookieV1,
	payloadId: string,
	now: Date
): Record<string, string> {
	const properties: Record<string, string> = {
		platform: 'web',
		payload_id: payloadId,
	};

	const includeWebTouch =
		!isTrustedUtmTimestamp(cookie) || canEmitSessionStartedAfterChange(cookie, now);

	if (includeWebTouch) {
		for (const key of [
			'utm_source',
			'utm_medium',
			'utm_campaign',
			'utm_content',
			'utm_term',
		] as const) {
			const value = cookie[key];
			if (value) {
				properties[key] = value;
			}
		}

		for (const [key, value] of Object.entries({
			gclid: cookie.gclid,
			fbclid: cookie.fbclid,
			irclickid: cookie.irclickid,
			ttclid: cookie.ttclid,
			rdt_cid: cookie.rdt_cid,
		})) {
			if (value) {
				properties[key] = value;
			}
		}
	}

	for (const [key, value] of Object.entries({
		fbp: cookie._fbp,
		fbc: cookie._fbc,
	})) {
		if (value) {
			properties[key] = value;
		}
	}

	const shareCode = effectiveShareCode(cookie);
	if (shareCode) {
		properties.share_code = shareCode;
	}

	if (
		isTrustedUtmTimestamp(cookie) &&
		canEmitSessionStartedAfterChange(cookie, now) &&
		cookie.utm_timestamp
	) {
		properties.utm_timestamp = cookie.utm_timestamp;
	}

	return properties;
}

/**
 * Creates a super cookie from URL params and existing cookies.
 * Reads UTMs, click IDs, promo codes, and Facebook pixel cookies.
 *
 * Campaign-identity UTMs are replaced only when URL `utm_source` is non-empty.
 * `utm_content` or `utm_term` alone updates in place without clearing `utm_source`.
 *
 * @returns Super cookie and whether Session_Started should fire after a successful write
 */
export function createSuperCookie(options?: CreateSuperCookieOptions): CreateSuperCookieResult {
	const now = options?.now ?? new Date();
	const beforeSnapshot = snapshotSessionTriggers(getSuperCookie());
	const existingCookie = getSuperCookie();
	const superCookie: SuperCookieV1 = existingCookie
		? { ...existingCookie, v: 1, frontend_platform: 'web' }
		: { v: 1, frontend_platform: 'web' };
	// Drop removed schema keys that may still exist in older cookie payloads.
	delete (superCookie as SuperCookieV1 & { discovery_quiz?: unknown }).discovery_quiz;

	// Read UTM parameters from URL
	const utmSource = getURLParam('utm_source');
	const utmMedium = getURLParam('utm_medium');
	const utmCampaign = getURLParam('utm_campaign');
	const utmContent = getURLParam('utm_content');
	const utmTerm = getURLParam('utm_term');
	const utmProvider = getURLParam('utm_provider');

	if (utmSource) {
		delete superCookie.utm_source;
		delete superCookie.utm_medium;
		delete superCookie.utm_campaign;
		delete superCookie.utm_content;
		delete superCookie.utm_term;
		delete superCookie.utm_provider;
	}

	if (utmSource) superCookie.utm_source = utmSource;
	if (utmMedium) superCookie.utm_medium = utmMedium;
	if (utmCampaign) superCookie.utm_campaign = utmCampaign;
	if (utmContent) superCookie.utm_content = utmContent;
	if (utmTerm) superCookie.utm_term = utmTerm;
	if (utmProvider) superCookie.utm_provider = utmProvider;

	// Read click IDs from URL (platform-independent: only update when present)
	const gclid = getURLParam('gclid');
	const fbclid = getURLParam('fbclid');
	const irclickid = getURLParam('irclickid');
	const ttclid = getURLParam('ttclid');
	const rdt_cid = getURLParam('rdt_cid');

	if (gclid) superCookie.gclid = gclid;
	if (fbclid) superCookie.fbclid = fbclid;
	if (irclickid) superCookie.irclickid = irclickid;
	if (ttclid) superCookie.ttclid = ttclid;
	if (rdt_cid) superCookie.rdt_cid = rdt_cid;

	// Update Facebook pixel cookies only when present in the browser.
	// Preserves existing super cookie values if the browser cookies are absent,
	// consistent with how other fields (UTMs, click IDs) are handled.
	const fbp = getCookie('_fbp');
	const fbc = getCookie('_fbc');
	if (fbp) superCookie._fbp = fbp;
	if (fbc) superCookie._fbc = fbc;

	// Read promo codes from URL params
	const stashCodeV2 = getURLParam('stash_code_v2');
	const stashCode = getURLParam('stash_code');

	if (stashCodeV2) superCookie.promoCode = stashCodeV2;
	if (stashCode) superCookie.legacyPromoCode = stashCode;

	const theme = getURLParam('theme');
	const channel = getURLParam('channel');
	const mixName = getURLParam('mixName');
	const prompt = getURLParam('prompt');
	if (theme) superCookie.theme = theme;
	if (channel) superCookie.channel = channel;
	if (mixName) superCookie.mixName = mixName;
	if (prompt) superCookie.prompt = prompt;

	// Fall back to individual promo code cookies if not set from URL
	if (!superCookie.promoCode) {
		const existingPromoCodeV2 = getCookie('stashPromoCodeV2');
		if (existingPromoCodeV2) superCookie.promoCode = existingPromoCodeV2;
	}
	if (!superCookie.legacyPromoCode) {
		const existingPromoCode = getCookie('stashPromoCode');
		if (existingPromoCode) superCookie.legacyPromoCode = existingPromoCode;
	}

	if (timestampTriggerChanged(beforeSnapshot, superCookie)) {
		superCookie.utm_timestamp = now.toISOString();
		superCookie.utm_timestamp_src = utmTimestampSrcForCookie(superCookie);
	}

	const hasData =
		superCookie.promoCode ||
		superCookie.legacyPromoCode ||
		superCookie.utm_source ||
		superCookie.utm_medium ||
		superCookie.utm_campaign ||
		superCookie.utm_content ||
		superCookie.utm_term ||
		superCookie.utm_provider ||
		superCookie.gclid ||
		superCookie.fbclid ||
		superCookie.irclickid ||
		superCookie.ttclid ||
		superCookie.rdt_cid ||
		superCookie._fbp ||
		superCookie._fbc ||
		superCookie.theme ||
		superCookie.channel ||
		superCookie.mixName ||
		superCookie.prompt;

	if (!hasData) {
		return { cookie: null, shouldEmitSessionStarted: false };
	}

	try {
		const cookieString = JSON.stringify(superCookie);
		const encodedValue = encodeURIComponent(cookieString);
		const cookieSizeBytes = encodedValue.length;

		if (cookieSizeBytes > MAX_COOKIE_SIZE_BYTES) {
			console.warn(
				`Super cookie size (${cookieSizeBytes} bytes encoded) exceeds limit (${MAX_COOKIE_SIZE_BYTES} bytes), skipping`
			);
			return { cookie: null, shouldEmitSessionStarted: false };
		}
	} catch (error) {
		console.warn('Failed to create super cookie:', error);
		return { cookie: null, shouldEmitSessionStarted: false };
	}

	const codeChanged = fieldValueChanged(
		effectiveShareCode(beforeSnapshot),
		effectiveShareCode(superCookie)
	);
	const attributionChanged = timestampTriggerChanged(beforeSnapshot, superCookie);
	const shouldEmitSessionStarted = codeChanged || attributionChanged;

	return { cookie: superCookie, shouldEmitSessionStarted };
}

/**
 * Sets the super cookie in the browser.
 * Cookie name: stashCookie, Path: /, Domain: .stash.com, Expires: 60 days
 */
export function setSuperCookie(superCookie: SuperCookieV1): void {
	try {
		const cookieString = JSON.stringify(superCookie);
		const encodedValue = encodeURIComponent(cookieString);
		setCookie(SUPER_COOKIE_NAME, encodedValue, {
			domain: '.stash.com',
			maxAge: Math.floor(SUPER_COOKIE_MAX_AGE_MS / 1000),
		});
	} catch (error) {
		console.warn('Failed to set super cookie:', error);
	}
}
